import React, { useRef, useState, useMemo } from 'react';
import {
  Button,
  VStack,
  Icon,
  HStack,
  Tooltip,
  Box,
  Flex,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components';
import { FormBuilder } from '@/components/formBuilder';
import { JobListMenu } from '@/components/ats/filters/components/UiItems/JobList';
import { useGetAllJobsListQuery } from '@/store/query/ats-jobs';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { KMenuButton } from '@/ui-library/components/atoms/KMenuButton/KMenuButton';

interface AssociateJobPopoverProps {
  onAssociate: (
    jobId: number,
    requisitionId?: number,
    applyToGroup?: boolean
  ) => void;
  currentJobId?: number;
  isLoading?: boolean;
  isEditing?: boolean;
  triggerElement?: React.ReactNode;
  isPartOfGroup?: boolean;
  groupRequisitions?: any[];
  currentRequisitionId?: number;
  variant?: 'tertiary' | 'primary' | 'unstyled';
}

export const AssociateJobPopover: React.FC<AssociateJobPopoverProps> = ({
  onAssociate,
  currentJobId,
  isLoading = false,
  isEditing = false,
  triggerElement,
  isPartOfGroup = false,
  groupRequisitions = [],
  currentRequisitionId,
  variant = 'tertiary',
}) => {
  const formRef = useRef<any>(null);
  const [selectedRequisitionId, setSelectedRequisitionId] = useState<
    number | undefined
  >(currentRequisitionId);
  const [showForm, setShowForm] = useState(false);
  const [applyToGroup, setApplyToGroup] = useState(false);
  const [menuKey, setMenuKey] = useState(0);

  const { data: atsJobs = [], isLoading: isJobsLoading } =
    useGetAllJobsListQuery({
      params: {
        scope: 'viewable',
      },
    });

  const tooltipLabel =
    'Requisitions associated with jobs will open automatically after approval, or immediately upon creation if no approval process is required';

  const formConfig = [
    {
      name: 'job',
      label: '',
      type: 'select',
      isRequired: true,
      errorMessage: 'Job is required',
      containerStyles: {},
      isLoading: isJobsLoading,
      menuPlacement: 'top',
      styleVersion: 'v1',
      useInput: true,
      options: atsJobs?.map((item: any) => ({
        value: item?.value || item?.id,
        label: item?.label || item?.name,
        status: item?.status || 'published',
        department: item?.department,
        locations: item?.locations || [],
      })),
      newStyles: [
        {
          type: 'menu',
          newStyles: {
            padding: '0px !important',
            borderRadius: '6px !important',
            border: '1px solid #EDEDED',
            borderColor: 'border-color',
            boxShadow: 'none',
          },
        },
      ],
      components: {
        Menu: JobListMenu,
        IndicatorSeparator: () => null,
      },
      excludeStatuses: ['archived', 'closed'],
      filterOption: (option: any, inputValue: string) => {
        if (!inputValue) return true;

        const normalizedSearchTerm = inputValue.toLowerCase().trim();
        const name = option.label?.toLowerCase() || '';
        const department = option.data?.department?.toLowerCase() || '';
        const locations =
          option.data?.locations
            ?.map((loc: any) => loc?.toLowerCase())
            .join(' ') || '';

        return (
          name.includes(normalizedSearchTerm) ||
          department.includes(normalizedSearchTerm) ||
          locations.includes(normalizedSearchTerm)
        );
      },
      placeholder: 'Select a job',
    },
  ];

  const initialValues = {
    job: currentJobId,
  };

  const handleMenuItemClick = (isApplyToGroup: boolean) => {
    setApplyToGroup(isApplyToGroup);
    setSelectedRequisitionId(isApplyToGroup ? undefined : currentRequisitionId);
    setShowForm(true);
  };

  const handleFormSubmit = (values: any) => {
    onAssociate(values.job, selectedRequisitionId, applyToGroup);
    // Force menu to close by changing key
    if (!isLoading) {
      setMenuKey((prev) => prev + 1);
    }
  };

  const title = isEditing ? 'Change associated job' : 'Associate with a job';
  const description = isEditing
    ? 'Select a different job to associate with this requisition'
    : 'Link this requisition to an existing job posting';

  // Filter out associated requisitions from the group
  const filteredGroupRequisitions = useMemo(() => {
    return groupRequisitions.filter((req) => !req.requisition_association?.id);
  }, [groupRequisitions]);

  // Get group requisition codes for display
  const groupRequisitionCodes = useMemo(() => {
    if (filteredGroupRequisitions.length > 0) {
      return filteredGroupRequisitions
        .map((req) => req.code)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }
    return [];
  }, [filteredGroupRequisitions]);

  // Menu items for group requisitions (similar to ReassignPopover pattern)
  const associateMenuItems = useMemo(() => {
    return [
      {
        id: 'associate_single',
        label: 'Associate this requisition alone',
        description: '',
        applyToGroup: false,
      },
      {
        id: 'associate_all',
        label: 'Associate this and all related requisitions',
        description: '',
        applyToGroup: true,
        groupRequisitionCodes: groupRequisitionCodes,
      },
    ];
  }, [groupRequisitionCodes]);

  // Menu content with form
  const menuContentWithForm = (
    <VStack align="stretch" spacing={4} p={4} minW="400px">
      {/* Header */}
      <VStack align="stretch" spacing={1}>
        <HStack align="center">
          <KText fontSize="md" fontWeight="semibold" color="secondary-900">
            {title}
          </KText>
          <Tooltip label={tooltipLabel} placement="top" hasArrow>
            <Box>
              <Icon
                as={InfoCircle}
                width="16px"
                height="16px"
                color="secondary-700"
                mt="-2px"
              />
            </Box>
          </Tooltip>
        </HStack>
        <KText fontSize="sm" color="secondary-600">
          {description}
        </KText>
      </VStack>

      {/* Form */}
      <FormBuilder
        formRef={formRef}
        config={formConfig}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        formContainerProps={{ marginTop: '0px !important' }}
      />

      {/* Submit Button */}
      <Flex gap="10px" justifyContent="flex-end" mt="0px !important">
        <Button
          variant="secondary"
          onClick={() => {
            if (
              isPartOfGroup &&
              filteredGroupRequisitions.length > 0 &&
              showForm
            ) {
              // In group mode, go back to menu items
              setShowForm(false);
              setSelectedRequisitionId(currentRequisitionId);
              setApplyToGroup(false);
            } else {
              // Close the menu by changing key
              setMenuKey((prev) => prev + 1);
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (formRef.current) {
              formRef.current.handleSubmit();
            }
          }}
          isLoading={isLoading}
        >
          {isEditing ? 'Change' : 'Associate'}
        </Button>
      </Flex>
    </VStack>
  );

  // Menu content for group mode - shows options first, then form when option is selected
  const groupMenuContent = showForm ? (
    <Box>{menuContentWithForm}</Box>
  ) : (
    <>
      {associateMenuItems.map((item, index) => (
        <Box
          key={item.id}
          p="12px"
          borderTop={index > 0 ? '1px solid' : undefined}
          borderColor={index > 0 ? 'border-color' : undefined}
          cursor="pointer"
          onClick={() => handleMenuItemClick(item.applyToGroup)}
          _hover={{ bg: 'secondary-50' }}
          role="group"
          className="group"
        >
          <Flex direction="column" gap="6px">
            <Flex direction="column" gap="3px">
              <KText size="sm-medium" color="secondary-900">
                {item.label}
              </KText>
              {item.description && (
                <KText size="sm-regular" color="secondary-700">
                  {item.description}
                </KText>
              )}
            </Flex>
            {item.applyToGroup &&
              item.groupRequisitionCodes &&
              item.groupRequisitionCodes.length > 0 && (
                <Flex flexWrap="wrap" gap="6px" maxH="200px" overflowY="auto">
                  {item.groupRequisitionCodes.map((code: string) => (
                    <KTag
                      key={code}
                      value={code}
                      color="secondary-700"
                      _groupHover={{
                        bgColor: 'white',
                        borderColor: 'border-color',
                        color: 'secondary-700',
                        borderWidth: '1px',
                      }}
                    />
                  ))}
                </Flex>
              )}
          </Flex>
        </Box>
      ))}
    </>
  );

  // When part of group, show menu items + form; otherwise show form directly
  const menuContent =
    isPartOfGroup && filteredGroupRequisitions.length > 0
      ? groupMenuContent
      : menuContentWithForm;

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <KMenuButton
        key={menuKey}
        variant={variant}
        triggerContent={
          triggerElement || (
            <Box>
              <KText color="primary-600" size="sm-medium">
                Associate with a job
              </KText>
            </Box>
          )
        }
        menuContentProps={{
          minW: '400px',
          maxW: '320px',
          boxShadow: 'drop-shadows-bottom',
          zIndex: 1500,
          borderRadius: '6px',
          p: '0px !important',
          onClick: (e: any) => {
            e.stopPropagation();
            e.preventDefault();
          },
        }}
        p="6px !important"
        {...(variant === 'tertiary' && {
          _hover: { bg: 'primary-100' },
          _active: { bg: 'primary-100' },
        })}
        iconProps={{ display: isPartOfGroup ? 'inline' : 'none' }}
        dividerProps={{ display: 'none' }}
        menuContent={menuContent}
        isPortal={true}
        onClose={() => {
          // Reset all state when menu closes
          setShowForm(false);
          setSelectedRequisitionId(currentRequisitionId);
          setApplyToGroup(false);
        }}
      />
    </Box>
  );
};
